/**
 * Document Generation Service
 * Handles all document generation API calls
 */
import axiosClient from './axiosClient';

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Document type definitions matching backend
 */
export const DOCUMENT_TYPES = {
  LEVEL1_MANUAL: 'level1_manual',
  IMPLEMENTATION_MANUAL: 'implementation_manual',
  FORM: 'form',
  PROCEDURE: 'procedure',
  IMPLEMENTATION_GUIDE: 'implementation_guide',
};

/**
 * Output format types
 */
export const OUTPUT_FORMATS = {
  DOCX: 'docx',
  CSV: 'csv',
};

class DocumentService {
  /**
   * Universal document generation endpoint
   * Supports all 5 document types
   *
   * @param {Object} payload
   * @param {number} payload.company_id - Company ID
   * @param {number} payload.iso_standard_id - ISO Standard ID
   * @param {string} payload.document_type - One of DOCUMENT_TYPES
   * @param {string} [payload.document_name] - Document name (required for forms/procedures)
   * @param {string} [payload.document_number] - Document number (required for forms/procedures)
   * @param {string} [payload.output_format] - Output format (docx or csv)
   * @param {Object} [payload.additional_params] - Additional parameters
   * @returns {Promise<Object>} Document generation result
   */
  async generateDocument(payload) {
    try {
      const response = await axiosClient.post(`${API_URL}/documents/generate`, payload);
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Generate Level-1 Management System Manual
   */
  async generateLevel1Manual({ companyId, isoStandardId, additionalParams = {} }) {
    return this.generateDocument({
      company_id: companyId,
      iso_standard_id: isoStandardId,
      document_type: DOCUMENT_TYPES.LEVEL1_MANUAL,
      output_format: OUTPUT_FORMATS.DOCX,
      additional_params: additionalParams,
    });
  }

  /**
   * Generate Implementation Manual
   */
  async generateImplementationManual({ companyId, isoStandardId, timeline = '6', additionalParams = {} }) {
    return this.generateDocument({
      company_id: companyId,
      iso_standard_id: isoStandardId,
      document_type: DOCUMENT_TYPES.IMPLEMENTATION_MANUAL,
      output_format: OUTPUT_FORMATS.DOCX,
      additional_params: {
        timeline,
        ...additionalParams,
      },
    });
  }

  /**
   * Generate Form (simplified endpoint)
   */
  async generateForm({ companyId, isoStandardId, formName, formNumber, formPurpose }) {
    try {
      const response = await axiosClient.post(`${API_URL}/documents/generate/form`, {
        company_id: companyId,
        iso_standard_id: isoStandardId,
        form_name: formName,
        form_number: formNumber,
        form_purpose: formPurpose,
      });
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Generate Procedure (simplified endpoint)
   */
  async generateProcedure({ companyId, isoStandardId, procedureName, procedureNumber, procedureScope }) {
    try {
      const response = await axiosClient.post(`${API_URL}/documents/generate/procedure`, {
        company_id: companyId,
        iso_standard_id: isoStandardId,
        procedure_name: procedureName,
        procedure_number: procedureNumber,
        procedure_scope: procedureScope,
      });
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Generate Implementation Guide
   */
  async generateImplementationGuide({ companyId, isoStandardId, timeline = '6', additionalParams = {} }) {
    return this.generateDocument({
      company_id: companyId,
      iso_standard_id: isoStandardId,
      document_type: DOCUMENT_TYPES.IMPLEMENTATION_GUIDE,
      output_format: OUTPUT_FORMATS.DOCX,
      additional_params: {
        timeline,
        ...additionalParams,
      },
    });
  }

  /**
   * Download a generated document
   *
   * @param {string} documentId - Document ID
   * @param {number} companyId - Company ID
   * @returns {Promise<Blob>} Document file
   */
  async downloadDocument(documentId, companyId) {
    try {
      const response = await axiosClient.get(`${API_URL}/documents/download/${documentId}`, {
        params: { company_id: companyId },
        responseType: 'blob',
      });

      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'document';

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      return {
        blob: response.data,
        filename,
      };
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * List all documents for a company
   *
   * @param {number} companyId - Company ID
   * @param {string} [documentType] - Filter by document type
   * @param {number} [limit=100] - Maximum results
   * @returns {Promise<Object>} Document list
   */
  async listDocuments(companyId, documentType = null, limit = 100) {
    try {
      const params = { limit };
      if (documentType) {
        params.document_type = documentType;
      }

      const response = await axiosClient.get(`${API_URL}/documents/list/${companyId}`, { params });
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Delete a document
   *
   * @param {string} documentId - Document ID
   * @param {number} companyId - Company ID
   * @returns {Promise<Object>} Delete result
   */
  async deleteDocument(documentId, companyId) {
    try {
      const response = await axiosClient.delete(`${API_URL}/documents/${documentId}`, {
        params: { company_id: companyId },
      });
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Trigger browser download of document
   *
   * @param {string} documentId - Document ID
   * @param {number} companyId - Company ID
   */
  async triggerDownload(documentId, companyId) {
    const { blob, filename } = await this.downloadDocument(documentId, companyId);

    // Create temporary link and trigger download
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Handle API errors
   * @private
   */
  _handleError(error) {
    const customError = new Error(error.userMessage || error.message);
    customError.originalError = error;
    customError.status = error.response?.status;
    customError.data = error.response?.data;
    return customError;
  }
}

// Export singleton instance
export default new DocumentService();
